import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { BiArrowBack, BiPlus, BiTrash } from 'react-icons/bi';
import useCustomer from '../hooks/useCustomer';
import useDeleteCustomer from '../hooks/useDeleteCustomer';
import useQuotes from '../hooks/useQuotes';
import useJobs from '../hooks/useJobs';
import DataTable from '../components/utility/dataTable';
import CustomerDetailsCard from '../components/customer/customerDetailsCard';
import AccountingSyncBadge from '../components/integrations/AccountingSyncBadge';
import CreateQuoteModal from '../components/quote/createQuoteModal';
import { formatDate, formatInstant, formatCurrency, formatCountry } from '../utils/formatters';

// Scoped Quotes/Jobs tables drop the Customer column locally — we're already
// on a per-customer page, repeating "Acme Ltd" in every row is noise. We do
// NOT edit the shared ALL_COLUMNS in quotesPage.jsx / jobsPage.jsx.
//
// Jobs carry per-row currency (a customer may have jobs in multiple
// currencies over time), so money cells format with the row's currency.
// Quotes don't have their own currency in v1 — they inherit from their
// customer — but the server projects CustomerCurrency onto each row so
// both tables can render money in the correct symbol without any extra
// plumbing from the page.
const jobMoney = (v, row) => formatCurrency(v, row?.currency);
const quoteMoney = (v, row) => formatCurrency(v, row?.customerCurrency);
const QUOTE_COLUMNS = [
    { key: 'quoteNumber', label: 'Quote #', system: true },
    { key: 'title',       label: 'Title',   system: false },
    { key: 'status',      label: 'Status',  system: false },
    { key: 'total',       label: 'Total',   system: false, render: quoteMoney },
    { key: 'quotedAt',    label: 'Quoted On',  system: false, render: formatDate },
    { key: 'expiryDate',  label: 'Expires On', system: false, render: formatDate },
    { key: 'createdAt',   label: 'Created On', system: false, render: formatInstant },
];
const QUOTE_DEFAULT_VISIBLE = {
    quoteNumber: true, title: true, status: true, total: true,
    quotedAt: true, expiryDate: false, createdAt: false,
};

const JOB_COLUMNS = [
    { key: 'jobNumber',     label: 'Job #',     system: true },
    { key: 'title',         label: 'Title',     system: false },
    { key: 'status',        label: 'Status',    system: false },
    { key: 'quotedRevenue', label: 'Quoted',    system: false, render: jobMoney },
    { key: 'estimatedCost', label: 'Est. Cost', system: false, render: jobMoney },
    { key: 'actualCost',    label: 'Cost',      system: false, render: jobMoney },
    { key: 'actualRevenue', label: 'Revenue',   system: false, render: jobMoney },
    { key: 'startDate',     label: 'Start',     system: false, render: formatDate },
    { key: 'endDate',       label: 'End',       system: false, render: formatDate },
    { key: 'createdAt',     label: 'Created',   system: false, render: formatInstant },
];
const JOB_DEFAULT_VISIBLE = {
    jobNumber: true, title: true, status: true, quotedRevenue: true,
    actualCost: true, startDate: true, estimatedCost: false,
    actualRevenue: false, endDate: false, createdAt: false,
};

// Default set: every non-system column that's visible by default. Mirrors
// the derivation inside DataTable so "Reset to Defaults" and the initial
// filter seed agree on the same set.
const QUOTE_DEFAULT_SEARCH_FIELDS = QUOTE_COLUMNS
    .filter(col => !col.system && QUOTE_DEFAULT_VISIBLE[col.key])
    .map(col => col.path ?? col.key);

const JOB_DEFAULT_SEARCH_FIELDS = JOB_COLUMNS
    .filter(col => !col.system && JOB_DEFAULT_VISIBLE[col.key])
    .map(col => col.path ?? col.key);

function loadSearchableFields(tableName, defaults) {
    try {
        const stored = localStorage.getItem(`${tableName}_search`);
        if (stored) return JSON.parse(stored);
    } catch { /* fall through to default */ }
    return defaults;
}

// Filter shape must match what useQuotes/useJobs and their APIs expect —
// adding customerId keys the React Query cache per-customer automatically.
// searchFields seeded from localStorage so the first query already has the
// persisted value — previously DataTable pushed them up on mount, which
// fired a wasted initial request with empty fields.
const quoteFilter = (customerId) => ({
    pageNumber: 1,
    pageSize: 20,
    sortOptions: { key: 'createdAt', direction: 'descending' },
    searchOptions: {
        searchTerm: '',
        searchFields: loadSearchableFields(`marginpal-quotes_customer_${customerId}`, QUOTE_DEFAULT_SEARCH_FIELDS),
    },
    statusFilter: null,
    customerId,
});

const jobFilter = (customerId) => ({
    pageNumber: 1,
    pageSize: 20,
    sortOptions: { key: 'createdAt', direction: 'descending' },
    searchOptions: {
        searchTerm: '',
        searchFields: loadSearchableFields(`marginpal-jobs_customer_${customerId}`, JOB_DEFAULT_SEARCH_FIELDS),
    },
    statusFilter: null,
    customerId,
});

function makeFilterHandler(setFilter, extraCases = {}) {
    return ({ filterId, value }) => {
        setFilter(prev => {
            if (extraCases[filterId]) return extraCases[filterId](prev, value);
            switch (filterId) {
                case 'pageNumber':
                    return { ...prev, pageNumber: Number(value) };
                case 'pageSize':
                    return { ...prev, pageSize: Number(value), pageNumber: 1 };
                case 'sortOptions':
                    return { ...prev, sortOptions: value, pageNumber: 1 };
                case 'search':
                    return { ...prev, pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchTerm: value } };
                case 'searchFields':
                    return { ...prev, pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchFields: value } };
                default:
                    return prev;
            }
        });
    };
}

export default function ViewCustomerPage() {
    const { id: idParam } = useParams();
    const id = Number(idParam);
    const navigate = useNavigate();

    // `dirtyRef` is shared between the customer query hook (so SignalR
    // invalidations pause while the user is editing) and the Details card
    // (which owns the dirty state). See useCustomer.js for the contract.
    const dirtyRef = useRef(false);
    const {
        data: customer, isLoading, error, flushPendingRefresh,
    } = useCustomer(id, dirtyRef);

    const [quoteFilterState, setQuoteFilterState] = useState(() => quoteFilter(id));
    const [jobFilterState, setJobFilterState] = useState(() => jobFilter(id));
    const [addQuoteOpen, setAddQuoteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const deleteCustomer = useDeleteCustomer();

    const handleDelete = () => {
        deleteCustomer.mutate(id, {
            onSuccess: () => {
                // Close modal before navigating so the Offcanvas/Modal fade
                // state doesn't carry over to the list page.
                setDeleteOpen(false);
                navigate('/customers');
            },
        });
    };

    const quotes = useQuotes(quoteFilterState);
    const jobs = useJobs(jobFilterState);

    const handleQuoteFilterChange = makeFilterHandler(setQuoteFilterState, {
        status: (prev, value) => ({ ...prev, statusFilter: value, pageNumber: 1 }),
    });
    const handleJobFilterChange = makeFilterHandler(setJobFilterState, {
        status: (prev, value) => ({ ...prev, statusFilter: value, pageNumber: 1 }),
    });

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    // fetchCustomerById attaches `status` to the error; 404 means either the
    // customer doesn't exist OR belongs to a different tenant (CompanyId
    // filter on the server). Show the same "not found" panel either way —
    // don't leak which it is.
    if (error?.status === 404 || (!customer && !isLoading)) {
        return (
            <div>
                <Link to="/customers" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Customers
                </Link>
                <div className="surface text-center py-5">
                    <h4 className="mb-2">Customer not found</h4>
                    <p className="text-muted mb-3">
                        This customer doesn't exist or you don't have access to it.
                    </p>
                    <Link to="/customers">
                        <Button color="primary">Back to Customers</Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (error) {
        // Genuine 5xx/network — let the user retry via reload.
        return (
            <div>
                <Link to="/customers" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Customers
                </Link>
                <div className="surface text-center py-5 text-danger">
                    Failed to load customer. Please try again.
                </div>
            </div>
        );
    }

    return (
        <div>
            <Link to="/customers" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-2">
                <BiArrowBack /> Customers
            </Link>
            {/* Header row — name/subtitle on the left, destructive action on
                the right. Using flex rather than positioning so the Delete
                button stays aligned with the heading baseline on mobile. */}
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1 className="fw-bold mb-1">{customer.name}</h1>
                    <p className="text-muted mb-0">
                        {[customer.contactName, formatCountry(customer.country)].filter(Boolean).join(' · ')}
                    </p>
                </div>
                <Button color="danger" outline size="sm" onClick={() => setDeleteOpen(true)}
                        className="d-inline-flex align-items-center gap-1 flex-shrink-0">
                    <BiTrash /> Delete
                </Button>
            </div>

            <div className="mb-4">
                <CustomerDetailsCard
                    customer={customer}
                    dirtyRef={dirtyRef}
                    flushPendingRefresh={flushPendingRefresh}
                />
                <div className="mt-2">
                    <AccountingSyncBadge type="Customer" id={id} />
                </div>
            </div>

            <h2 className="fw-semibold fs-4 mb-3">Quotes</h2>
            <div className="mb-4">
                <DataTable
                    tableName={`marginpal-quotes_customer_${id}`}
                    columns={QUOTE_COLUMNS.filter(c => QUOTE_DEFAULT_VISIBLE[c.key])}
                    data={quotes.data?.items ?? []}
                    isLoading={quotes.isLoading}
                    error={quotes.error}
                    totalRecords={quotes.data?.totalRecords ?? 0}
                    filter={quoteFilterState}
                    filters={[]}
                    actions={[
                        <Button color="primary" size="sm" onClick={() => setAddQuoteOpen(true)}
                                className="d-inline-flex align-items-center gap-1">
                            <BiPlus /> Add Quote
                        </Button>
                    ]}
                    visibleColumns={QUOTE_DEFAULT_VISIBLE}
                    defaultVisibleColumns={QUOTE_DEFAULT_VISIBLE}
                    settingsColumns={QUOTE_COLUMNS}
                    onFilterChange={handleQuoteFilterChange}
                    onRowClick={(e, row) => navigate(`/quotes/${row.id}`)}
                    sortConfiguration={{ key: 'createdAt', direction: 'descending' }}
                />
            </div>

            <h2 className="fw-semibold fs-4 mb-3">Jobs</h2>
            <div>
                <DataTable
                    tableName={`marginpal-jobs_customer_${id}`}
                    columns={JOB_COLUMNS.filter(c => JOB_DEFAULT_VISIBLE[c.key])}
                    data={jobs.data?.items ?? []}
                    isLoading={jobs.isLoading}
                    error={jobs.error}
                    totalRecords={jobs.data?.totalRecords ?? 0}
                    filter={jobFilterState}
                    filters={[]}
                    onRowClick={(e, row) => navigate(`/jobs/${row.id}`)}
                    actions={[]}
                    visibleColumns={JOB_DEFAULT_VISIBLE}
                    defaultVisibleColumns={JOB_DEFAULT_VISIBLE}
                    settingsColumns={JOB_COLUMNS}
                    onFilterChange={handleJobFilterChange}
                    sortConfiguration={{ key: 'createdAt', direction: 'descending' }}
                />
            </div>

            <CreateQuoteModal
                isOpen={addQuoteOpen}
                onClose={() => setAddQuoteOpen(false)}
                customerId={id}
            />

            {/* Destructive-action confirm. Backdrop is static (click-outside
                doesn't dismiss) so a stray click can't trigger a cascading
                delete. Header/footer explicitly call out that related quotes
                and jobs go with the customer — we don't want a surprise. */}
            <Modal isOpen={deleteOpen} toggle={() => !deleteCustomer.isPending && setDeleteOpen(false)}
                   centered backdrop="static">
                <ModalHeader toggle={() => !deleteCustomer.isPending && setDeleteOpen(false)}>
                    Delete customer?
                </ModalHeader>
                <ModalBody>
                    <p className="mb-2">
                        Permanently delete <strong>{customer.name}</strong>?
                    </p>
                    <p className="text-muted mb-0 small">
                        This also deletes every quote and job attached to this customer,
                        along with their line items, expenses, and receipts. This cannot
                        be undone.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setDeleteOpen(false)}
                            disabled={deleteCustomer.isPending}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDelete}
                            disabled={deleteCustomer.isPending}>
                        {deleteCustomer.isPending && <Spinner size="sm" className="me-1" />}
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
